import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Stat, TableWrap, Btn, Badge, Avatar, Card, ExportMenu } from "../shared/components"
import {
  listEnrollments,
  listClassEnrollments,
  generateParticipantQR,
  emailParticipantQR,
  createParticipant,
  downloadParticipantImportTemplate,
  downloadParticipantImportTemplateXlsx,
  downloadParticipantImportTemplatePdf,
  importParticipantsCSV,
  listProgramTrainers,
  assignTrainer,
  unassignTrainer,
} from "../api/programs.api"
import { deleteUser, updateUser, lookupUserByEmail, getMaritimeCert, updateMaritimeCert, exportUserData, eraseUserData } from "../api/users.api"

export default function UsersScreen() {
  const { lang, user: me, currentProgramId, currentProgram, currentClassId, currentClass, branding } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  const [tab, setTab] = React.useState("participants") // "participants" | "trainers"

  // ── Participants state ────────────────────────────────────────
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [previewing, setPreviewing] = React.useState(null)
  const [busyId, setBusyId] = React.useState(null)
  const [showAddModal, setShowAddModal] = React.useState(false)
  const [showImportModal, setShowImportModal] = React.useState(false)
  const [editCertFor, setEditCertFor] = React.useState(null) // { id, name } | null

  // ── Trainers state ────────────────────────────────────────────
  const [trainers, setTrainers] = React.useState([])
  const [trainersLoading, setTrainersLoading] = React.useState(false)
  const [showAssignModal, setShowAssignModal] = React.useState(false)
  const [unassignBusyId, setUnassignBusyId] = React.useState(null)

  // Dengan kelas terpilih, daftarnya hanya peserta angkatan itu. Tanpa kelas
  // ("Semua kelas"), seluruh peserta program ikut tampil — masih dibutuhkan
  // untuk melihat lintas angkatan.
  const reload = React.useCallback(() => {
    if (!currentProgramId) return
    setLoading(true)
    const ambil = currentClassId
      ? listClassEnrollments(currentProgramId, currentClassId)
      : listEnrollments(currentProgramId)
    ambil
      .then(setRows)
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [currentProgramId, currentClassId])

  const reloadTrainers = React.useCallback(() => {
    if (!currentProgramId) return
    setTrainersLoading(true)
    listProgramTrainers(currentProgramId)
      .then(setTrainers)
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setTrainersLoading(false))
  }, [currentProgramId])

  React.useEffect(() => { reload() }, [reload])
  React.useEffect(() => { reloadTrainers() }, [reloadTrainers])

  const handlePreviewQR = async (e) => {
    setBusyId(e.id)
    try {
      const res = await generateParticipantQR(currentProgramId, e.id)
      setPreviewing({
        qrPngDataUrl: res.qrPngDataUrl,
        qrToken: res.qrToken,
        name: e.user?.full_name,
        email: e.user?.email,
        expiresAt: res.expiresAt,
      })
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (e) => {
    const name = e.user?.full_name || e.user?.email || "user ini"
    if (!window.confirm(
      lang === "id"
        ? `Hapus ${name} permanen? Semua enrollment, presensi, sertifikat, dan progress-nya akan ikut terhapus.`
        : `Permanently delete ${name}? All enrollments, attendance, certificates, and progress will be removed.`
    )) return
    setBusyId(e.id)
    try {
      await deleteUser(e.user.id)
      toast.success(lang === "id" ? "Peserta dihapus" : "Participant deleted")
      reload()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusyId(null)
    }
  }

  // Permintaan pemilik data (UU PDP): salinan seluruh data pribadinya.
  const handleExportData = async (e) => {
    setBusyId(e.id)
    try {
      const { blob, filename } = await exportUserData(e.user.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      a.click()
      URL.revokeObjectURL(url)
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusyId(null)
    }
  }

  // Penghapusan data pribadi = anonimisasi. Berbeda dari tombol hapus permanen:
  // nilai dan kehadiran tetap untuk laporan program, identitas dan berkasnya
  // hilang. Tidak bisa dibatalkan, jadi email akunnya harus diketik ulang.
  const handleEraseData = async (e) => {
    const email = e.user?.email || ""
    const ketik = window.prompt(
      lang === "id"
        ? `Hapus DATA PRIBADI ${e.user?.full_name || email}?\n\nNama, email, telepon, tanggal lahir, perusahaan, lokasi absen, percakapan AI, dan berkas sertifikatnya dihapus. Nilai dan kehadiran tetap ada tanpa identitas. Tidak bisa dibatalkan.\n\nKetik email akun ini untuk melanjutkan:`
        : `Erase PERSONAL DATA of ${e.user?.full_name || email}?\n\nName, email, phone, date of birth, company, attendance location, AI chats, and certificate files are erased. Scores and attendance remain, without identity. This cannot be undone.\n\nType this account's email to continue:`
    )
    if (ketik === null) return
    setBusyId(e.id)
    try {
      await eraseUserData(e.user.id, ketik)
      toast.success(lang === "id" ? "Data pribadi dihapus" : "Personal data erased")
      reload()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleEmailQR = async (e) => {
    if (!window.confirm(
      lang === "id"
        ? `Kirim QR login ke ${e.user?.email}?`
        : `Send login QR to ${e.user?.email}?`
    )) return
    setBusyId(e.id)
    try {
      const res = await emailParticipantQR(currentProgramId, e.id)
      toast.success(
        res.mocked
          ? (lang === "id" ? `Email di-mock (RESEND_API_KEY kosong). Lihat console backend.` : `Mocked. Check backend console.`)
          : (lang === "id" ? `QR dikirim ke ${res.sentTo}` : `QR sent to ${res.sentTo}`)
      )
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusyId(null)
    }
  }

  // Menonaktifkan akun trainer: ia tidak bisa masuk lagi dan sesinya dicabut,
  // tetapi riwayat sesi dan penugasannya utuh, dan bisa diaktifkan kembali.
  const handleToggleTrainerActive = async (t) => {
    const name = t.user?.full_name || t.user?.email || "trainer ini"
    const aktifkan = t.user?.is_active === false
    if (!aktifkan && !window.confirm(
      lang === "id"
        ? `Nonaktifkan akun ${name}? Ia tidak bisa masuk lagi di program mana pun sampai diaktifkan kembali. Riwayat sesinya tetap ada.`
        : `Deactivate ${name}'s account? They will be unable to sign in to any program until reactivated. Their session history is kept.`
    )) return
    setUnassignBusyId(t.id)
    try {
      await updateUser(t.user.id, { isActive: aktifkan })
      toast.success(aktifkan
        ? (lang === "id" ? "Akun trainer diaktifkan" : "Trainer account reactivated")
        : (lang === "id" ? "Akun trainer dinonaktifkan" : "Trainer account deactivated"))
      reloadTrainers()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setUnassignBusyId(null)
    }
  }

  // Hapus permanen. Server menolak (409) bila trainer pernah memegang sesi atau
  // batch sertifikat — dan itu justru yang diharapkan: riwayatnya tidak boleh
  // hilang diam-diam. Pesannya mengarahkan ke "Nonaktifkan".
  const handleDeleteTrainer = async (t) => {
    const name = t.user?.full_name || t.user?.email || "trainer ini"
    if (!window.confirm(
      lang === "id"
        ? `Hapus akun ${name} PERMANEN? Ia dihapus dari semua program dan tidak bisa dibatalkan. Kalau hanya ingin ia berhenti bekerja, pilih "Nonaktifkan".`
        : `Permanently delete ${name}'s account? They are removed from every program and this cannot be undone. If you only want them to stop working, choose "Deactivate".`
    )) return
    setUnassignBusyId(t.id)
    try {
      await deleteUser(t.user.id)
      toast.success(lang === "id" ? "Akun trainer dihapus" : "Trainer account deleted")
      reloadTrainers()
    } catch (ex) {
      if (ex?.response?.status === 409) {
        toast.error(lang === "id"
          ? "Tidak bisa dihapus: trainer ini punya riwayat sesi atau sertifikat. Gunakan \"Nonaktifkan\" sebagai gantinya."
          : "Cannot delete: this trainer has session or certificate history. Use \"Deactivate\" instead.")
      } else {
        toast.error(ex?.response?.data?.error || ex.message)
      }
    } finally {
      setUnassignBusyId(null)
    }
  }

  const handleUnassignTrainer = async (t) => {
    const name = t.user?.full_name || t.user?.email || "trainer ini"
    if (!window.confirm(
      lang === "id"
        ? `Lepas penugasan ${name} dari program ini?`
        : `Unassign ${name} from this program?`
    )) return
    setUnassignBusyId(t.id)
    try {
      await unassignTrainer(currentProgramId, t.user.id)
      toast.success(lang === "id" ? "Trainer dilepas" : "Trainer unassigned")
      reloadTrainers()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setUnassignBusyId(null)
    }
  }

  const canManageTrainerAccount = (t) =>
    ["SUPER_ADMIN", "PROGRAM_ADMIN", "PLATFORM_OWNER"].includes(me?.role) &&
    t.user?.role === "TRAINER" &&
    t.user?.id !== me?.id

  const totalParticipants = rows.length
  const withCert = rows.filter((r) => r.certificate?.claimed_at).length

  return (
    <div className="main-inner">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-text">
          <div className="eyebrow">
            {currentProgram?.code?.toUpperCase() || orgName} · {lang === "id" ? "Pengguna" : "Users"}
          </div>
          <h1 className="h1" style={{ marginTop: 6 }}>
            {lang === "id" ? "Peserta & Trainer" : "Participants & Trainers"}
          </h1>
        </div>
        <div className="page-header-actions">
          {tab === "participants" ? (
            <>
              <Btn variant="secondary" icon={<Icon name="upload" size={14}/>} onClick={() => setShowImportModal(true)} disabled={!currentProgramId}>
                {lang === "id" ? "Import CSV" : "Import CSV"}
              </Btn>
              <Btn variant="primary" icon={<Icon name="plus" size={14}/>} onClick={() => setShowAddModal(true)} disabled={!currentProgramId}>
                {lang === "id" ? "Tambah Peserta" : "Add Participant"}
              </Btn>
            </>
          ) : (
            <Btn variant="primary" icon={<Icon name="plus" size={14}/>} onClick={() => setShowAssignModal(true)} disabled={!currentProgramId}>
              {lang === "id" ? "Assign Trainer" : "Assign Trainer"}
            </Btn>
          )}
        </div>
      </div>

      {/* Tab switcher */}
      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border-subtle)", marginBottom: 20 }}>
        {[
          { id: "participants", label: lang === "id" ? `Peserta (${totalParticipants})` : `Participants (${totalParticipants})` },
          { id: "trainers",     label: lang === "id" ? `Trainer (${trainers.length})` : `Trainers (${trainers.length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              background: "none", border: "none", cursor: "pointer", padding: "10px 16px",
              fontSize: 13, fontWeight: 500,
              color: tab === t.id ? "var(--text)" : "var(--text-3)",
              borderBottom: tab === t.id ? "2px solid var(--program-color, #B84E2A)" : "2px solid transparent",
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Participants tab ── */}
      {tab === "participants" && <>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
          <Stat label={lang === "id" ? "Total peserta" : "Total participants"} value={String(totalParticipants)} />
          <Stat label={lang === "id" ? "Sertifikat diklaim" : "Cert claimed"} value={String(withCert)} />
          <Stat label={lang === "id" ? "Belum klaim" : "Pending claim"} value={String(totalParticipants - withCert)} />
        </div>
      </>}

      {/* Participants table */}
      {tab === "participants" && <div className="card mt-4">
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Daftar Peserta" : "Participants"}</div>
          <span className="muted" style={{ fontSize: 13 }}>{rows.length}</span>
        </div>
        {loading ? (
          <div className="card-body muted small" style={{textAlign:"center"}}>
            {lang === "id" ? "Memuat..." : "Loading..."}
          </div>
        ) : rows.length === 0 ? (
          <div className="card-body muted small" style={{textAlign:"center"}}>
            {lang === "id" ? "Belum ada peserta terdaftar." : "No participants enrolled."}
          </div>
        ) : (
          <TableWrap>
          <table className="tbl">
            <thead><tr>
              <th>{lang === "id" ? "Peserta" : "Participant"}</th>
              <th>{lang === "id" ? "Email" : "Email"}</th>
              <th>{lang === "id" ? "Terdaftar" : "Enrolled"}</th>
              <th>{lang === "id" ? "Sertifikat" : "Certificate"}</th>
              <th></th>
            </tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td>
                    <div className="row gap-2" style={{ alignItems: "center" }}>
                      <Avatar name={e.user?.full_name || e.user?.email || "?"} size={28} />
                      <div style={{ fontWeight: 500 }}>{e.user?.full_name || "—"}</div>
                    </div>
                  </td>
                  <td className="small muted">{e.user?.email || "—"}</td>
                  <td className="small muted mono">
                    {new Date(e.enrolled_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US")}
                  </td>
                  <td>
                    {e.certificate?.claimed_at
                      ? <Badge tone="success" dot>{lang === "id" ? "Diklaim" : "Claimed"}</Badge>
                      : e.certificate
                        ? <Badge tone="brand">{lang === "id" ? "Tersedia" : "Available"}</Badge>
                        : <Badge>—</Badge>}
                  </td>
                  <td>
                    <div className="row gap-2">
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="qrCode" size={12}/>}
                        disabled={busyId === e.id}
                        onClick={() => handlePreviewQR(e)}
                      >
                        QR
                      </Btn>
                      <Btn
                        variant="secondary"
                        size="sm"
                        icon={<Icon name="send" size={12}/>}
                        disabled={busyId === e.id}
                        onClick={() => handleEmailQR(e)}
                      >
                        {lang === "id" ? "Email" : "Email"}
                      </Btn>
                      {/* Hanya institusi maritim yang butuh koreksi sertifikat
                          Nautika/Hubla di luar re-import CSV — sembunyikan
                          sama sekali untuk yang lain, sama seperti kolomnya
                          disembunyikan di wizard import (EnrollmentScreen). */}
                      {branding?.maritime_fields && (
                        <Btn
                          variant="ghost"
                          size="sm"
                          icon={<Icon name="edit" size={12}/>}
                          disabled={busyId === e.id}
                          onClick={() => setEditCertFor({ id: e.user?.id, name: e.user?.full_name || e.user?.email })}
                        />
                      )}
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="download" size={12}/>}
                        title={lang === "id" ? "Ekspor data pribadi (JSON)" : "Export personal data (JSON)"}
                        disabled={busyId === e.id}
                        onClick={() => handleExportData(e)}
                      />
                      {me?.role === "SUPER_ADMIN" || me?.role === "PLATFORM_OWNER" ? (
                        <Btn
                          variant="ghost"
                          size="sm"
                          icon={<Icon name="shield" size={12}/>}
                          title={lang === "id" ? "Hapus data pribadi (anonimisasi)" : "Erase personal data (anonymise)"}
                          disabled={busyId === e.id}
                          onClick={() => handleEraseData(e)}
                        />
                      ) : null}
                      <Btn
                        variant="ghost"
                        size="sm"
                        icon={<Icon name="trash" size={12}/>}
                        disabled={busyId === e.id}
                        onClick={() => handleDelete(e)}
                        style={{ color: "var(--danger)" }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </TableWrap>
        )}
      </div>}

      {/* ── Trainers tab ── */}
      {tab === "trainers" && (
        <div className="card mt-0">
          <div className="card-header">
            <div className="h3">{lang === "id" ? "Trainer Ditugaskan" : "Assigned Trainers"}</div>
            <span className="muted" style={{ fontSize: 13 }}>{trainers.length}</span>
          </div>
          {trainersLoading ? (
            <div className="card-body muted small" style={{ textAlign: "center" }}>
              {lang === "id" ? "Memuat..." : "Loading..."}
            </div>
          ) : trainers.length === 0 ? (
            <div className="card-body muted small" style={{ textAlign: "center", padding: 32 }}>
              {lang === "id"
                ? 'Belum ada trainer ditugaskan. Klik "Assign Trainer" untuk menambahkan.'
                : 'No trainers assigned yet. Click "Assign Trainer" to add one.'}
            </div>
          ) : (
            <TableWrap>
              <table className="tbl">
                <thead><tr>
                  <th>{lang === "id" ? "Trainer" : "Trainer"}</th>
                  <th>Email</th>
                  <th>{lang === "id" ? "Ditugaskan" : "Assigned"}</th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {trainers.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <div className="row gap-2" style={{ alignItems: "center" }}>
                          <Avatar name={t.user?.full_name || t.user?.email || "?"} size={28} />
                          <div style={{ fontWeight: 500 }}>{t.user?.full_name || "—"}</div>
                        </div>
                      </td>
                      <td className="small muted">
                        {t.user?.email || "—"}
                        {t.user?.is_active === false && (
                          <> <Badge tone="warning">{lang === "id" ? "Nonaktif" : "Inactive"}</Badge></>
                        )}
                      </td>
                      <td className="small muted mono">
                        {new Date(t.assigned_at).toLocaleDateString(lang === "id" ? "id-ID" : "en-US")}
                      </td>
                      <td>
                        <div className="row gap-2" style={{ justifyContent: "flex-end" }}>
                          {/* Akun hanya bisa dikelola dari sini bila perannya TRAINER
                              (super admin yang kebetulan ditugaskan sebagai trainer
                              tidak) dan bukan akun sendiri. Server menjaga hal yang
                              sama; ini supaya tombol yang pasti ditolak tidak muncul. */}
                          {canManageTrainerAccount(t) && (
                            <>
                              <Btn
                                variant="ghost"
                                size="sm"
                                disabled={unassignBusyId === t.id}
                                onClick={() => handleToggleTrainerActive(t)}
                              >
                                {t.user?.is_active === false
                                  ? (lang === "id" ? "Aktifkan" : "Reactivate")
                                  : (lang === "id" ? "Nonaktifkan" : "Deactivate")}
                              </Btn>
                              <Btn
                                variant="ghost"
                                size="sm"
                                icon={<Icon name="trash" size={12}/>}
                                disabled={unassignBusyId === t.id}
                                onClick={() => handleDeleteTrainer(t)}
                                style={{ color: "var(--danger)" }}
                              >
                                {lang === "id" ? "Hapus akun" : "Delete account"}
                              </Btn>
                            </>
                          )}
                          <Btn
                            variant="ghost"
                            size="sm"
                            disabled={unassignBusyId === t.id}
                            onClick={() => handleUnassignTrainer(t)}
                          >
                            {lang === "id" ? "Lepas dari program" : "Unassign"}
                          </Btn>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </div>
      )}

      {previewing && (
        <QRPreviewModal data={previewing} onClose={() => setPreviewing(null)} />
      )}

      {showAddModal && (
        <AddParticipantModal
          programId={currentProgramId}
          programName={currentProgram?.name}
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); reload() }}
        />
      )}

      {showImportModal && (
        <ImportCSVModal
          programId={currentProgramId}
          programName={currentProgram?.name}
          onClose={() => setShowImportModal(false)}
          onImported={() => reload()}
        />
      )}

      {showAssignModal && (
        <AssignTrainerModal
          programId={currentProgramId}
          programName={currentProgram?.name}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => { setShowAssignModal(false); reloadTrainers() }}
        />
      )}

      {editCertFor && (
        <EditMaritimeCertModal
          userId={editCertFor.id}
          userName={editCertFor.name}
          onClose={() => setEditCertFor(null)}
          onSaved={() => { setEditCertFor(null); reload() }}
        />
      )}
    </div>
  )
}

function ImportCSVModal({ programId, programName, onClose, onImported }) {
  const { lang } = useApp()
  const [file, setFile] = React.useState(null)
  const [sendQrEmail, setSendQrEmail] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [result, setResult] = React.useState(null)
  const [err, setErr] = React.useState(null)
  const [downloadingTpl, setDownloadingTpl] = React.useState(null)

  const handleDownloadTemplate = async (kind) => {
    setDownloadingTpl(kind)
    try {
      const fn = {
        "template-csv": downloadParticipantImportTemplate,
        "template-xlsx": downloadParticipantImportTemplateXlsx,
        "template-pdf": downloadParticipantImportTemplatePdf,
      }[kind]
      if (!fn) return
      const blob = await fn(programId)
      const ext = kind.split("-").pop()
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = `template-impor-peserta.${ext}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      if (e?.response?.status === 503) {
        toast.error(lang === "id" ? "Ekspor PDF sedang tidak tersedia" : "PDF export unavailable")
      } else {
        toast.error(e?.response?.data?.error || e.message)
      }
    } finally {
      setDownloadingTpl(null)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!file) return
    setBusy(true)
    setErr(null)
    setResult(null)
    try {
      const res = await importParticipantsCSV(programId, file, { sendQrEmail })
      setResult(res)
      onImported()
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "grid", placeItems: "center", zIndex: 50 }}
      onClick={onClose}
    >
      <div
        className="tile"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 560, maxHeight: "90vh", overflow: "auto", background: "var(--surface)" }}
      >
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Import Peserta dari CSV" : "Import Participants from CSV"}</div>
          <Btn type="button" variant="ghost" size="sm" onClick={onClose}>
            <Icon name="close" size={14}/>
          </Btn>
        </div>
        <form onSubmit={handleSubmit} className="col gap-3" style={{ padding: 16 }}>
          <p className="small muted">
            {lang === "id"
              ? `Import peserta secara batch ke program ${programName || ""}. Wajib pakai template CSV.`
              : `Bulk import participants into ${programName || "this program"}. Use the CSV template.`}
          </p>

          <div style={{ alignSelf: "flex-start" }}>
            <ExportMenu
              formats={["csv", "xlsx", "pdf"]}
              kindPrefix="template"
              busyKind={downloadingTpl}
              onSelect={handleDownloadTemplate}
              lang={lang}
              label={lang === "id" ? "Unduh template" : "Download template"}
            />
          </div>

          {err && <div className="banner red"><Icon name="alert"/>{err}</div>}

          {!result && (
            <>
              <label className="col gap-1">
                <span className="small">{lang === "id" ? "Berkas CSV" : "CSV file"}</span>
                <input
                  type="file"
                  accept=".csv,.xlsx"
                  required
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                />
                <span className="tiny muted">
                  {lang === "id"
                    ? "Format: 13 kolom (email, client_company, client_id, ...). Lihat template."
                    : "Format: 13 columns (email, client_company, client_id, ...). See template."}
                </span>
              </label>

              <label className="row gap-2 align-center">
                <input
                  type="checkbox"
                  checked={sendQrEmail}
                  onChange={(e) => setSendQrEmail(e.target.checked)}
                />
                <span className="small">
                  {lang === "id"
                    ? "Kirim QR login via email ke setiap peserta yang baru di-enroll"
                    : "Email login QR to each newly enrolled participant"}
                </span>
              </label>
            </>
          )}

          {result && (
            <div className="col gap-2">
              <div className="banner green">
                <Icon name="check"/>
                {lang === "id"
                  ? `Selesai. ${result.created} dibuat, ${result.enrolled} di-enroll, ${result.updated} diperbarui, ${result.skipped} dilewati.`
                  : `Done. ${result.created} created, ${result.enrolled} enrolled, ${result.updated} updated, ${result.skipped} skipped.`}
              </div>
              {result.errors?.length > 0 && (
                <div className="card" style={{ maxHeight: 220, overflow: "auto" }}>
                  <div className="card-header">
                    <div className="h3">
                      {lang === "id" ? "Baris bermasalah" : "Problem rows"} ({result.errors.length})
                    </div>
                  </div>
                  <TableWrap>
                  <table className="tbl">
                    <thead><tr>
                      <th>{lang === "id" ? "Baris" : "Row"}</th>
                      <th>Email</th>
                      <th>{lang === "id" ? "Alasan" : "Reason"}</th>
                    </tr></thead>
                    <tbody>
                      {result.errors.map((er, idx) => (
                        <tr key={idx}>
                          <td>{er.row}</td>
                          <td>{er.email || "—"}</td>
                          <td className="small">{er.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </TableWrap>
                </div>
              )}
            </div>
          )}
        </form>
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {result ? (lang === "id" ? "Tutup" : "Close") : (lang === "id" ? "Batal" : "Cancel")}
          </Btn>
          {!result && (
            <Btn type="button" variant="primary" size="sm" onClick={handleSubmit} disabled={busy || !file}>
              {busy
                ? (lang === "id" ? "Memproses..." : "Importing...")
                : (lang === "id" ? "Mulai Import" : "Start Import")}
            </Btn>
          )}
        </div>
      </div>
    </div>
  )
}

// Isi/koreksi sertifikat Nautika & Hubla satu peserta — jalur di luar
// re-import CSV massal, yang satu-satunya sebelum ini bisa menulis field
// tersebut. Kolom yang dikosongkan di formulir ini BERARTI mengosongkan
// nilainya di server (beda dari semantik "kosong = jangan sentuh" saat
// import), sesuai catatan di PATCH /users/:id/maritime-cert.
function EditMaritimeCertModal({ userId, userName, onClose, onSaved }) {
  const { lang } = useApp()
  const [loading, setLoading] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [nauticalCertNo, setNauticalCertNo] = React.useState("")
  const [nauticalCertExpiresAt, setNauticalCertExpiresAt] = React.useState("")
  const [hublaCertNo, setHublaCertNo] = React.useState("")
  const [hublaCertExpiresAt, setHublaCertExpiresAt] = React.useState("")

  // ISO datetime dari server -> yyyy-mm-dd yang dimengerti <input type="date">.
  const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "")

  React.useEffect(() => {
    if (!userId) return
    getMaritimeCert(userId)
      .then((d) => {
        setNauticalCertNo(d.nauticalCertNo || "")
        setNauticalCertExpiresAt(toDateInput(d.nauticalCertExpiresAt))
        setHublaCertNo(d.hublaCertNo || "")
        setHublaCertExpiresAt(toDateInput(d.hublaCertExpiresAt))
      })
      .catch((e) => toast.error(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false))
  }, [userId])

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      await updateMaritimeCert(userId, {
        nauticalCertNo, nauticalCertExpiresAt, hublaCertNo, hublaCertExpiresAt,
      })
      toast.success(lang === "id" ? "Sertifikat tersimpan" : "Certificate saved")
      onSaved()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
        display: "grid", placeItems: "center", zIndex: 50,
      }}
    >
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="card" style={{ width: 440, background: "var(--surface)" }}>
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Sertifikat Maritim" : "Maritime Certificate"}</div>
          <Btn type="button" variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14}/></Btn>
        </div>
        <div className="col gap-3" style={{ padding: 16 }}>
          <div className="small muted">{userName}</div>
          {loading ? (
            <div className="small muted" style={{ textAlign: "center", padding: 12 }}>
              {lang === "id" ? "Memuat..." : "Loading..."}
            </div>
          ) : (
            <>
              <div className="field">
                <label className="field-label">{lang === "id" ? "No. Sertifikat Nautika" : "Nautical Cert No"}</label>
                <input className="input" value={nauticalCertNo} onChange={(e) => setNauticalCertNo(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">{lang === "id" ? "Kedaluwarsa Nautika" : "Nautical Expires"}</label>
                <input className="input" type="date" value={nauticalCertExpiresAt} onChange={(e) => setNauticalCertExpiresAt(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">{lang === "id" ? "No. Sertifikat Hubla" : "Hubla Cert No"}</label>
                <input className="input" value={hublaCertNo} onChange={(e) => setHublaCertNo(e.target.value)} />
              </div>
              <div className="field">
                <label className="field-label">{lang === "id" ? "Kedaluwarsa Hubla" : "Hubla Expires"}</label>
                <input className="input" type="date" value={hublaCertExpiresAt} onChange={(e) => setHublaCertExpiresAt(e.target.value)} />
              </div>
            </>
          )}
          <Btn type="submit" disabled={loading || busy}>
            {busy ? (lang === "id" ? "Menyimpan..." : "Saving...") : (lang === "id" ? "Simpan" : "Save")}
          </Btn>
        </div>
      </form>
    </div>
  )
}

function AddParticipantModal({ programId, programName, onClose, onCreated }) {
  const { lang } = useApp()
  const [email, setEmail] = React.useState("")
  const [fullName, setFullName] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [autoPassword, setAutoPassword] = React.useState(true)
  const [sendQrEmail, setSendQrEmail] = React.useState(true)
  const [busy, setBusy] = React.useState(false)
  const [result, setResult] = React.useState(null)

  const submit = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const payload = {
        email,
        fullName,
        sendQrEmail,
      }
      if (!autoPassword && password) payload.password = password
      const res = await createParticipant(programId, payload)
      setResult(res)
      toast.success(lang === "id" ? "Peserta berhasil ditambah" : "Participant created")
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  if (result) {
    return (
      <div
        className="modal-overlay"
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
          display: "grid", placeItems: "center", zIndex: 50,
        }}
      >
        <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 480, background: "var(--surface)" }}>
          <div className="card-header">
            <div className="h3">{lang === "id" ? "Peserta Berhasil Ditambah" : "Participant Created"}</div>
            <Btn variant="ghost" size="sm" onClick={onCreated}><Icon name="close" size={14}/></Btn>
          </div>
          <div className="col gap-3" style={{ padding: 16 }}>
            <div>
              <div className="small muted">{lang === "id" ? "Nama" : "Name"}</div>
              <div><strong>{result.enrollment.user.full_name}</strong></div>
            </div>
            <div>
              <div className="small muted">{lang === "id" ? "Email" : "Email"}</div>
              <div className="mono small">{result.enrollment.user.email}</div>
            </div>
            {result.password && (
              <div style={{ padding: 12, background: "var(--amber-10, #FFF8E1)", border: "1px solid var(--amber-50, #FBC02D)" }}>
                <div className="small" style={{ fontWeight: 500, marginBottom: 4 }}>
                  {lang === "id" ? "Password Auto-Generate (catat sekarang!)" : "Auto-generated Password (save now!)"}
                </div>
                <div className="mono" style={{ fontSize: 14, userSelect: "all" }}>{result.password}</div>
                <div className="tiny muted mt-1">
                  {lang === "id"
                    ? "Password ini hanya muncul sekali. Salin sekarang atau peserta gunakan QR login."
                    : "Password shown once. Copy now or use QR login."}
                </div>
              </div>
            )}
            {result.emailResult && (
              <div style={{ padding: 12, background: "var(--green-10, #E8F5E9)", border: "1px solid var(--green-50, #4CAF50)" }}>
                <div className="row gap-2">
                  <Icon name="check" size={16} color="var(--green-60, #2E7D32)"/>
                  <span className="small">
                    {result.emailResult.mocked
                      ? (lang === "id" ? "Email QR mocked ke console (RESEND_API_KEY off)" : "Email mocked to console")
                      : (lang === "id" ? "Email QR login terkirim ke peserta" : "Login QR emailed to participant")}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end" }}>
            <Btn variant="primary" onClick={onCreated}>{lang === "id" ? "Selesai" : "Done"}</Btn>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.5)",
        display: "grid", placeItems: "center", zIndex: 50,
      }}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card"
        style={{ width: 480, background: "var(--surface)" }}
      >
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Tambah Peserta Baru" : "Add New Participant"}</div>
          <Btn type="button" variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14}/></Btn>
        </div>

        <div className="col gap-3" style={{ padding: 16 }}>
          <div className="tiny muted">
            {lang === "id"
              ? `Akan didaftarkan ke program: ${programName || "—"}`
              : `Will be enrolled to: ${programName || "—"}`}
          </div>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Nama Lengkap" : "Full Name"}</span>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              placeholder="Budi Santoso"
              style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
            />
          </label>

          <label className="col gap-1">
            <span className="small">{lang === "id" ? "Email" : "Email"}</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="budi@example.com"
              style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
            />
          </label>

          <label className="row gap-2">
            <input
              type="checkbox"
              checked={autoPassword}
              onChange={(e) => setAutoPassword(e.target.checked)}
            />
            <span className="small">{lang === "id" ? "Auto-generate password" : "Auto-generate password"}</span>
          </label>

          {!autoPassword && (
            <label className="col gap-1">
              <span className="small">{lang === "id" ? "Password (min 6)" : "Password (min 6)"}</span>
              <input
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                minLength={6}
                required
                placeholder="min. 6 karakter"
                style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
              />
            </label>
          )}

          <label className="row gap-2">
            <input
              type="checkbox"
              checked={sendQrEmail}
              onChange={(e) => setSendQrEmail(e.target.checked)}
            />
            <span className="small">
              {lang === "id"
                ? "Kirim QR login otomatis via email"
                : "Auto-send login QR via email"}
            </span>
          </label>
        </div>

        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {lang === "id" ? "Batal" : "Cancel"}
          </Btn>
          <Btn type="submit" variant="primary" size="sm" disabled={busy || !email || !fullName}>
            {busy
              ? (lang === "id" ? "Memproses..." : "Processing...")
              : (lang === "id" ? "Tambah Peserta" : "Add Participant")}
          </Btn>
        </div>
      </form>
    </div>
  )
}

function QRPreviewModal({ data, onClose }) {
  const { lang } = useApp()
  const downloadHref = data.qrPngDataUrl
  const downloadName = `login-qr-${data.name?.replace(/\s+/g, "_") || "user"}.png`

  const copyToken = () => {
    navigator.clipboard?.writeText(data.qrToken)
    toast.success(lang === "id" ? "Token disalin" : "Token copied")
  }

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
        display: "grid", placeItems: "center", zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 440, background: "var(--surface)" }}
      >
        <div className="card-header">
          <div className="h3">{lang === "id" ? "QR Login Peserta" : "Participant Login QR"}</div>
          <Btn variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14}/></Btn>
        </div>
        <div className="col" style={{ alignItems: "center", padding: 20, gap: 12 }}>
          <img
            src={data.qrPngDataUrl}
            alt="Login QR"
            style={{ width: "min(280px, 70vw)", aspectRatio: "1 / 1", border: "1px solid var(--border-subtle)", borderRadius: 8 }}
          />
          <div className="col" style={{ alignItems: "center", gap: 2 }}>
            <strong>{data.name}</strong>
            <span className="tiny muted">{data.email}</span>
            <span className="tiny muted mono">
              {lang === "id" ? "Berlaku sampai" : "Valid until"}: {new Date(data.expiresAt).toLocaleDateString(lang === "id" ? "id-ID" : "en-US")}
            </span>
          </div>
        </div>
        <div style={{ padding: "12px 16px", borderTop: "1px solid var(--border-subtle)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Btn variant="ghost" size="sm" icon={<Icon name="copy" size={12}/>} onClick={copyToken}>
            {lang === "id" ? "Salin token" : "Copy token"}
          </Btn>
          <a href={downloadHref} download={downloadName} style={{textDecoration:"none", display:"inline-flex", alignItems:"center", gap:6, padding:"4px 9px", fontSize:12, fontWeight:500, background:"var(--surface)", color:"var(--text)", border:"1px solid var(--border)", borderRadius:"var(--r-md)", boxShadow:"var(--shadow-xs)"}}>
            <Icon name="download" size={12}/>{lang === "id" ? "Download PNG" : "Download PNG"}
          </a>
        </div>
      </div>
    </div>
  )
}

function AssignTrainerModal({ programId, programName, onClose, onAssigned }) {
  const { lang } = useApp()
  const [email, setEmail] = React.useState("")
  const [fullName, setFullName] = React.useState("")
  const [found, setFound] = React.useState(null)   // null | false | user object
  const [looking, setLooking] = React.useState(false)
  const [busy, setBusy] = React.useState(false)
  const [result, setResult] = React.useState(null)

  const handleLookup = async (e) => {
    e.preventDefault()
    if (!email) return
    setLooking(true)
    setFound(null)
    setFullName("")
    try {
      const user = await lookupUserByEmail(email)
      setFound(user)
      setFullName(user.full_name)
    } catch (ex) {
      if (ex?.response?.status === 404) setFound(false)
      else toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setLooking(false)
    }
  }

  const handleAssign = async (e) => {
    e.preventDefault()
    setBusy(true)
    try {
      const payload = found ? { email } : { email, fullName }
      const res = await assignTrainer(programId, payload)
      setResult(res)
      onAssigned()
    } catch (ex) {
      toast.error(ex?.response?.data?.error || ex.message)
    } finally {
      setBusy(false)
    }
  }

  const searched = found !== null

  return (
    <div
      className="modal-overlay"
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "grid", placeItems: "center", zIndex: 50 }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{ width: 480, background: "var(--surface)" }}
      >
        <div className="card-header">
          <div className="h3">{lang === "id" ? "Assign Trainer" : "Assign Trainer"}</div>
          <Btn variant="ghost" size="sm" onClick={onClose}><Icon name="close" size={14}/></Btn>
        </div>

        {result ? (
          <div className="col gap-3" style={{ padding: 20 }}>
            <div style={{ background: "var(--green-10,#E8F5E9)", border: "1px solid var(--green-50,#4CAF50)", borderRadius: 6, padding: "12px 14px", display: "flex", gap: 8, alignItems: "center" }}>
              <Icon name="check" size={16} style={{ color: "var(--green-60,#2E7D32)", flexShrink: 0 }} />
              <span className="small">
                <strong>{result.assignment?.user?.full_name}</strong>
                {" "}{lang === "id" ? "berhasil ditugaskan ke" : "assigned to"}{" "}
                <strong>{programName}</strong>.
                {" "}{lang === "id" ? "Notifikasi email terkirim." : "Email notification sent."}
              </span>
            </div>
            {result.password && (
              <div style={{ padding: 12, background: "var(--amber-10,#FFF8E1)", border: "1px solid var(--amber-50,#FBC02D)", borderRadius: 6 }}>
                <div className="small" style={{ fontWeight: 500, marginBottom: 4 }}>
                  {lang === "id" ? "Password Sementara (catat sekarang!)" : "Temporary Password (save now!)"}
                </div>
                <div className="mono" style={{ fontSize: 14, userSelect: "all" }}>{result.password}</div>
                <div className="tiny muted mt-1">
                  {lang === "id"
                    ? "Password ini tercantum di email trainer. Trainer harus mengganti password setelah login pertama."
                    : "This password is in the trainer's email. They should change it after first login."}
                </div>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="primary" onClick={onClose}>{lang === "id" ? "Tutup" : "Close"}</Btn>
            </div>
          </div>
        ) : (
          <div className="col gap-4" style={{ padding: 20 }}>
            <p className="small muted" style={{ margin: 0 }}>
              {lang === "id"
                ? `Masukkan email trainer untuk program ${programName || "ini"}. Jika belum punya akun, akun akan dibuat otomatis.`
                : `Enter the trainer's email for ${programName || "this program"}. If they don't have an account yet, one will be created automatically.`}
            </p>

            <form onSubmit={handleLookup} className="col gap-2">
              <label className="col gap-1">
                <span className="small">{lang === "id" ? "Email Trainer" : "Trainer Email"}</span>
                <div className="row gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setFound(null); setFullName("") }}
                    required
                    placeholder="trainer@example.com"
                    style={{ flex: 1, height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
                  />
                  <Btn type="submit" variant="secondary" size="sm" disabled={looking || !email}>
                    {looking ? "..." : (lang === "id" ? "Cari" : "Search")}
                  </Btn>
                </div>
              </label>
            </form>

            {searched && found && (
              <div style={{ padding: 10, background: "var(--surface-2,#F8FAFC)", border: "1px solid var(--border-subtle)", borderRadius: 6 }}>
                <div className="small" style={{ marginBottom: 6, color: "var(--text-3)" }}>
                  {lang === "id" ? "Pengguna ditemukan:" : "User found:"}
                </div>
                <div className="row gap-2" style={{ alignItems: "center" }}>
                  <Avatar name={found.full_name} size={24} />
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{found.full_name}</div>
                    <div className="tiny muted">{found.role}</div>
                  </div>
                </div>
                {found.role !== "TRAINER" && (
                  <div className="tiny muted mt-1">
                    {lang === "id"
                      ? "Role akan diubah menjadi TRAINER saat di-assign."
                      : "Their role will be changed to TRAINER upon assignment."}
                  </div>
                )}
              </div>
            )}

            {searched && found === false && (
              <div className="col gap-2">
                <div style={{ padding: 10, background: "var(--amber-10,#FFF8E1)", border: "1px solid var(--amber-30,#FFE082)", borderRadius: 6 }}>
                  <div className="small" style={{ color: "var(--text-2)" }}>
                    {lang === "id"
                      ? "Email belum terdaftar — akun trainer baru akan dibuat."
                      : "Email not found — a new trainer account will be created."}
                  </div>
                </div>
                <label className="col gap-1">
                  <span className="small">{lang === "id" ? "Nama Lengkap" : "Full Name"} <span style={{ color: "var(--danger)" }}>*</span></span>
                  <input
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    required
                    placeholder={lang === "id" ? "Nama lengkap trainer" : "Trainer's full name"}
                    style={{ height: 32, padding: "0 8px", border: "1px solid var(--border-strong)" }}
                  />
                </label>
              </div>
            )}

            {searched && (
              <form onSubmit={handleAssign}>
                <Btn
                  type="submit"
                  variant="primary"
                  disabled={busy || (found === false && !fullName)}
                  style={{ width: "100%" }}
                >
                  {busy
                    ? (lang === "id" ? "Memproses..." : "Processing...")
                    : found
                      ? (lang === "id" ? `Assign ${found.full_name}` : `Assign ${found.full_name}`)
                      : (lang === "id" ? "Buat Akun & Assign" : "Create Account & Assign")}
                </Btn>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
